import React, { useEffect, useState, useMemo } from "react";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, Btn, Input, Select, Textarea, Modal, Card, Badge, EmptyState, fmtMoney, fmtDate } from "@/components/ui-kit";
import { Plus, Trash2, FileText, Printer, ArrowRightCircle, Send, CheckCircle2, XCircle, Eye } from "lucide-react";

const STATUS_COLOR = {
  draft: "slate", sent: "blue", accepted: "green",
  rejected: "red", expired: "amber", converted: "purple", cancelled: "red",
};

const CUSTOMER_TYPES = [
  { value: "pharmacy", label: "Pharmacy" },
  { value: "clinic", label: "Clinic" },
  { value: "hospital", label: "Hospital" },
  { value: "warehouse", label: "Warehouse" },
  { value: "wholesaler", label: "Wholesaler" },
  { value: "distributor", label: "Distributor" },
  { value: "doctor", label: "Doctor (direct)" },
  { value: "government", label: "Government Entity" },
  { value: "other", label: "Other" },
];

const newItem = () => ({
  product_id: "", product_name: "", sku: "", description: "",
  quantity: 1, unit_price: 0, discount_pct: 0, vat_rate: 14,
});

const emptyForm = {
  customer_id: "", customer_name: "", customer_type: "pharmacy",
  customer_tax_id: "", customer_address: "", customer_phone: "",
  items: [newItem()], valid_until: "", payment_terms: "Net 30",
  delivery_terms: "", notes: "", global_discount_pct: 0,
};

export default function Quotations() {
  const { user } = useAuth();
  const has = (p) => user?.permissions?.includes(p);

  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState({ status: "" });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewOpen, setViewOpen] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const params = {};
    if (filter.status) params.status = filter.status;
    const [q, c, p] = await Promise.all([
      api.get("/quotations", { params }),
      api.get("/customers"),
      api.get("/products"),
    ]);
    setQuotes(q.data); setCustomers(c.data); setProducts(p.data);
  };
  useEffect(() => { load(); }, [filter.status]);

  const totals = useMemo(() => {
    let sub = 0, vat = 0;
    form.items.forEach((it) => {
      const q = parseFloat(it.quantity) || 0;
      const u = parseFloat(it.unit_price) || 0;
      const d = parseFloat(it.discount_pct) || 0;
      const v = parseFloat(it.vat_rate) || 0;
      const s = q * u * (1 - d / 100);
      sub += s; vat += s * (v / 100);
    });
    const gd = parseFloat(form.global_discount_pct) || 0;
    const subAfter = sub * (1 - gd / 100);
    const vatAfter = vat * (1 - gd / 100);
    return {
      subBefore: sub, gdAmt: sub - subAfter, subAfter,
      vat: vatAfter, grand: subAfter + vatAfter,
    };
  }, [form.items, form.global_discount_pct]);

  const updateItem = (idx, key, val) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [key]: val };
    if (key === "product_id") {
      const p = products.find((pp) => pp.id === val);
      if (p) {
        items[idx].product_name = p.name;
        items[idx].sku = p.sku;
        items[idx].unit_price = p.pharmacy_price || 0;
        items[idx].vat_rate = p.vat_rate ?? 14;
      }
    }
    setForm({ ...form, items });
  };

  const onPickCustomer = (cid) => {
    const c = customers.find((cc) => cc.id === cid);
    setForm({
      ...form,
      customer_id: cid,
      customer_name: c?.name || "",
      customer_type: c?.customer_type || "pharmacy",
      customer_tax_id: c?.tax_registration_number || "",
      customer_address: c?.address || "",
      customer_phone: c?.phone || "",
    });
  };

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (q) => {
    setEditing(q);
    setForm({
      customer_id: q.customer_id || "",
      customer_name: q.customer_name || "",
      customer_type: q.customer_type || "pharmacy",
      customer_tax_id: q.customer_tax_id || "",
      customer_address: q.customer_address || "",
      customer_phone: q.customer_phone || "",
      items: (q.items || []).map((i) => ({
        product_id: i.product_id || "", product_name: i.product_name || "", sku: i.sku || "",
        description: i.description || "", quantity: i.quantity, unit_price: i.unit_price,
        discount_pct: i.discount_pct || 0, vat_rate: i.vat_rate || 14,
      })),
      valid_until: q.valid_until || "",
      payment_terms: q.payment_terms || "",
      delivery_terms: q.delivery_terms || "",
      notes: q.notes || "",
      global_discount_pct: q.global_discount_pct || 0,
    });
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name?.trim()) { alert("Customer name is required"); return; }
    if (form.items.length === 0) { alert("Add at least one item"); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        global_discount_pct: parseFloat(form.global_discount_pct) || 0,
        items: form.items.map((i) => ({
          ...i,
          product_id: i.product_id || null,
          quantity: parseFloat(i.quantity) || 0,
          unit_price: parseFloat(i.unit_price) || 0,
          discount_pct: parseFloat(i.discount_pct) || 0,
          vat_rate: parseFloat(i.vat_rate) || 0,
        })),
      };
      if (editing) await api.put(`/quotations/${editing.id}`, payload);
      else await api.post("/quotations", payload);
      setOpen(false);
      await load();
    } catch (err) {
      alert("Error: " + (err.response?.data?.detail || err.message));
    } finally { setBusy(false); }
  };

  const setStatus = async (id, status) => {
    if (status === "cancelled" && !window.confirm("Cancel this quotation?")) return;
    await api.put(`/quotations/${id}/status`, { status });
    await load();
  };

  const convert = async (q) => {
    if (!window.confirm(`Convert quotation ${q.number} into a Tax Invoice? This will finalize and issue the invoice.`)) return;
    try {
      const { data } = await api.post(`/quotations/${q.id}/convert`);
      alert(`Tax Invoice ${data.number} created successfully.`);
      await load();
    } catch (err) {
      alert("Convert failed: " + (err.response?.data?.detail || err.message));
    }
  };

  const remove = async (q) => {
    if (!window.confirm(`Delete quotation ${q.number}?`)) return;
    try {
      await api.delete(`/quotations/${q.id}`);
      await load();
    } catch (err) {
      alert("Delete failed: " + (err.response?.data?.detail || err.message));
    }
  };

  const openPdf = (q) => {
    const token = localStorage.getItem("access_token");
    const url = `${API_BASE}/quotations/${q.id}/pdf${token ? "?_t=" + Date.now() : ""}`;
    // Open in new tab; backend uses cookie/Bearer — use fetch+blob to authorize
    fetch(url, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((r) => r.blob())
      .then((b) => {
        const u = URL.createObjectURL(b);
        window.open(u, "_blank");
      });
  };

  return (
    <div data-testid="quotations-page">
      <PageHeader
        title="Quotations"
        subtitle="Create official quotations for pharmacies, warehouses, clinics, hospitals and more"
        actions={
          has("quotations.create") && (
            <Btn onClick={openCreate} data-testid="quotation-add-btn">
              <Plus className="w-4 h-4" /> New Quotation
            </Btn>
          )
        }
      />

      <Card className="p-4 mb-4">
        <div className="flex gap-3 flex-wrap">
          <Select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })} data-testid="quotation-status-filter">
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
            <option value="expired">Expired</option>
            <option value="converted">Converted</option>
            <option value="cancelled">Cancelled</option>
          </Select>
        </div>
      </Card>

      {quotes.length === 0 ? (
        <EmptyState message="No quotations yet." />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-600 dark:text-slate-300 text-xs uppercase tracking-wide">
              <tr>
                <th className="px-4 py-3 text-start">Number</th>
                <th className="px-4 py-3 text-start">Customer</th>
                <th className="px-4 py-3 text-start">Type</th>
                <th className="px-4 py-3 text-start">Date</th>
                <th className="px-4 py-3 text-start">Valid Until</th>
                <th className="px-4 py-3 text-end">Total (incl. VAT)</th>
                <th className="px-4 py-3 text-start">Status</th>
                <th className="px-4 py-3 text-end">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {quotes.map((q) => (
                <tr key={q.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/40" data-testid={`quotation-row-${q.id}`}>
                  <td className="px-4 py-3 font-mono text-xs font-semibold text-blue-700 dark:text-blue-300">{q.number}</td>
                  <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{q.customer_name}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{q.customer_type}</td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300 text-xs">{fmtDate(q.created_at)}</td>
                  <td className="px-4 py-3 text-slate-600 dark:text-slate-300 text-xs">{q.valid_until || "-"}</td>
                  <td className="px-4 py-3 text-end font-semibold">{fmtMoney(q.grand_total)} EGP</td>
                  <td className="px-4 py-3"><Badge color={STATUS_COLOR[q.status]}>{q.status}</Badge></td>
                  <td className="px-4 py-3 text-end">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => setViewOpen(q)} title="View" className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700" data-testid={`quotation-view-${q.id}`}>
                        <Eye className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                      </button>
                      <button onClick={() => openPdf(q)} title="Print/PDF" className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700" data-testid={`quotation-pdf-${q.id}`}>
                        <Printer className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                      </button>
                      {has("quotations.edit") && q.status === "draft" && (
                        <button onClick={() => setStatus(q.id, "sent")} title="Mark as Sent" className="p-1.5 rounded hover:bg-blue-50 dark:hover:bg-blue-950/40">
                          <Send className="w-4 h-4 text-blue-600" />
                        </button>
                      )}
                      {has("quotations.edit") && ["sent", "draft"].includes(q.status) && (
                        <>
                          <button onClick={() => setStatus(q.id, "accepted")} title="Accept" className="p-1.5 rounded hover:bg-emerald-50 dark:hover:bg-emerald-950/40">
                            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          </button>
                          <button onClick={() => setStatus(q.id, "rejected")} title="Reject" className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-950/40">
                            <XCircle className="w-4 h-4 text-red-600" />
                          </button>
                        </>
                      )}
                      {has("quotations.convert") && q.status === "accepted" && !q.converted_invoice_id && (
                        <button onClick={() => convert(q)} title="Convert to Tax Invoice" className="p-1.5 rounded hover:bg-purple-50 dark:hover:bg-purple-950/40" data-testid={`quotation-convert-${q.id}`}>
                          <ArrowRightCircle className="w-4 h-4 text-purple-700" />
                        </button>
                      )}
                      {has("quotations.edit") && ["draft", "sent"].includes(q.status) && (
                        <button onClick={() => openEdit(q)} title="Edit" className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700">
                          <FileText className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                        </button>
                      )}
                      {has("quotations.delete") && q.status !== "converted" && (
                        <button onClick={() => remove(q)} title="Delete" className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-950/40">
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? `Edit Quotation ${editing.number}` : "New Quotation"} maxWidth="max-w-5xl">
        <form onSubmit={submit} className="space-y-4" data-testid="quotation-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Select label="Existing Customer (optional)" value={form.customer_id} onChange={(e) => onPickCustomer(e.target.value)} data-testid="quotation-customer-select">
              <option value="">— Select to auto-fill —</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.customer_type})</option>)}
            </Select>
            <Input label="Customer Name *" value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} required data-testid="quotation-customer-name" />
            <Select label="Customer Type" value={form.customer_type} onChange={(e) => setForm({ ...form, customer_type: e.target.value })}>
              {CUSTOMER_TYPES.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
            <Input label="Tax ID" value={form.customer_tax_id} onChange={(e) => setForm({ ...form, customer_tax_id: e.target.value })} />
            <Input label="Phone" value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} />
            <Input label="Address" value={form.customer_address} onChange={(e) => setForm({ ...form, customer_address: e.target.value })} />
          </div>

          {/* Items table */}
          <div className="rounded-md ring-1 ring-slate-200 dark:ring-slate-700 overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                <tr>
                  <th className="px-2 py-2 text-start">Product / Description</th>
                  <th className="px-2 py-2 text-end">Qty</th>
                  <th className="px-2 py-2 text-end">Unit Price</th>
                  <th className="px-2 py-2 text-end">Disc %</th>
                  <th className="px-2 py-2 text-end">VAT %</th>
                  <th className="px-2 py-2 text-end">Subtotal</th>
                  <th className="px-2 py-2 text-end">VAT</th>
                  <th className="px-2 py-2 text-end">Total</th>
                  <th></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {form.items.map((it, i) => {
                  const q = parseFloat(it.quantity) || 0, u = parseFloat(it.unit_price) || 0;
                  const d = parseFloat(it.discount_pct) || 0, v = parseFloat(it.vat_rate) || 0;
                  const s = q * u * (1 - d / 100); const vt = s * (v / 100);
                  return (
                    <tr key={i} className="align-top">
                      <td className="px-2 py-2 min-w-[220px]">
                        <select
                          className="block w-full rounded border-slate-300 text-xs mb-1 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                          value={it.product_id}
                          onChange={(e) => updateItem(i, "product_id", e.target.value)}
                          data-testid={`quotation-item-product-${i}`}
                        >
                          <option value="">Select product or use custom text</option>
                          {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>)}
                        </select>
                        <input
                          className="block w-full rounded border-slate-300 text-xs dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                          placeholder="Description (optional)"
                          value={it.product_name}
                          onChange={(e) => updateItem(i, "product_name", e.target.value)}
                        />
                      </td>
                      <td className="px-2 py-2 w-20"><input type="number" min="0" step="any" className="w-full rounded border-slate-300 text-end text-xs dark:bg-slate-800 dark:border-slate-700 dark:text-white" value={it.quantity} onChange={(e) => updateItem(i, "quantity", e.target.value)} /></td>
                      <td className="px-2 py-2 w-24"><input type="number" min="0" step="any" className="w-full rounded border-slate-300 text-end text-xs dark:bg-slate-800 dark:border-slate-700 dark:text-white" value={it.unit_price} onChange={(e) => updateItem(i, "unit_price", e.target.value)} /></td>
                      <td className="px-2 py-2 w-16"><input type="number" min="0" max="100" step="0.01" className="w-full rounded border-slate-300 text-end text-xs dark:bg-slate-800 dark:border-slate-700 dark:text-white" value={it.discount_pct} onChange={(e) => updateItem(i, "discount_pct", e.target.value)} /></td>
                      <td className="px-2 py-2 w-16"><input type="number" min="0" step="0.01" className="w-full rounded border-slate-300 text-end text-xs dark:bg-slate-800 dark:border-slate-700 dark:text-white" value={it.vat_rate} onChange={(e) => updateItem(i, "vat_rate", e.target.value)} /></td>
                      <td className="px-2 py-2 text-end font-mono">{fmtMoney(s)}</td>
                      <td className="px-2 py-2 text-end font-mono text-slate-500">{fmtMoney(vt)}</td>
                      <td className="px-2 py-2 text-end font-mono font-semibold">{fmtMoney(s + vt)}</td>
                      <td className="px-2 py-2">
                        <button type="button" onClick={() => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) })} className="text-red-600 p-1">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="p-2 border-t border-slate-200 dark:border-slate-700">
              <Btn type="button" variant="secondary" onClick={() => setForm({ ...form, items: [...form.items, newItem()] })} data-testid="quotation-add-item">
                <Plus className="w-4 h-4" /> Add Item
              </Btn>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input label="Valid Until" type="date" value={form.valid_until} onChange={(e) => setForm({ ...form, valid_until: e.target.value })} />
            <Input label="Payment Terms" value={form.payment_terms} onChange={(e) => setForm({ ...form, payment_terms: e.target.value })} />
            <Input label="Delivery Terms" value={form.delivery_terms} onChange={(e) => setForm({ ...form, delivery_terms: e.target.value })} />
            <Input label="Global Discount %" type="number" min="0" max="100" step="0.01" value={form.global_discount_pct} onChange={(e) => setForm({ ...form, global_discount_pct: e.target.value })} />
            <div className="md:col-span-2">
              <Textarea label="Notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>

          <div className="bg-slate-50 dark:bg-slate-800/60 rounded-md p-4 text-sm">
            <div className="flex justify-between"><span>Subtotal (before discount)</span><span className="font-mono">{fmtMoney(totals.subBefore)} EGP</span></div>
            <div className="flex justify-between"><span>Global Discount</span><span className="font-mono">- {fmtMoney(totals.gdAmt)} EGP</span></div>
            <div className="flex justify-between"><span>Subtotal (after discount)</span><span className="font-mono">{fmtMoney(totals.subAfter)} EGP</span></div>
            <div className="flex justify-between"><span>VAT Total</span><span className="font-mono">{fmtMoney(totals.vat)} EGP</span></div>
            <div className="flex justify-between font-bold text-base mt-2 pt-2 border-t border-slate-300 dark:border-slate-600">
              <span>Grand Total</span><span className="font-mono text-blue-700 dark:text-blue-300">{fmtMoney(totals.grand)} EGP</span>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200 dark:border-slate-700">
            <Btn type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn type="submit" disabled={busy} data-testid="quotation-submit">{busy ? "Saving..." : (editing ? "Update Quotation" : "Create Quotation")}</Btn>
          </div>
        </form>
      </Modal>

      {/* Quick view modal */}
      <Modal open={!!viewOpen} onClose={() => setViewOpen(null)} title={viewOpen?.number || "Quotation"} maxWidth="max-w-3xl">
        {viewOpen && (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-2">
              <div><div className="text-xs text-slate-500">Customer</div><div className="font-medium">{viewOpen.customer_name}</div></div>
              <div><div className="text-xs text-slate-500">Type</div><div>{viewOpen.customer_type}</div></div>
              <div><div className="text-xs text-slate-500">Tax ID</div><div>{viewOpen.customer_tax_id || "-"}</div></div>
              <div><div className="text-xs text-slate-500">Status</div><Badge color={STATUS_COLOR[viewOpen.status]}>{viewOpen.status}</Badge></div>
              <div><div className="text-xs text-slate-500">Valid Until</div><div>{viewOpen.valid_until || "-"}</div></div>
              <div><div className="text-xs text-slate-500">Grand Total</div><div className="font-bold">{fmtMoney(viewOpen.grand_total)} EGP</div></div>
            </div>
            <div className="rounded-md ring-1 ring-slate-200 dark:ring-slate-700 overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800">
                  <tr><th className="px-2 py-1.5 text-start">Item</th><th className="px-2 py-1.5 text-end">Qty</th><th className="px-2 py-1.5 text-end">Unit</th><th className="px-2 py-1.5 text-end">VAT</th><th className="px-2 py-1.5 text-end">Total</th></tr>
                </thead>
                <tbody>
                  {viewOpen.items.map((it, i) => (
                    <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="px-2 py-1.5">{it.product_name}</td>
                      <td className="px-2 py-1.5 text-end">{it.quantity}</td>
                      <td className="px-2 py-1.5 text-end">{fmtMoney(it.unit_price)}</td>
                      <td className="px-2 py-1.5 text-end">{fmtMoney(it.line_vat)}</td>
                      <td className="px-2 py-1.5 text-end font-semibold">{fmtMoney(it.line_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end">
              <Btn onClick={() => openPdf(viewOpen)}><Printer className="w-4 h-4" /> Open PDF</Btn>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
